import { NavigationBarItem } from '@/components/NavigationBar/types';
import { navigationPaths } from '@/constants/navigationPaths';

export type TurbineFromLocationState = {
  siteId: string;
  locationName: string;
};

type TurbineNavigationParams = {
  name?: string | null;
};

export const buildTurbineQueryParams = ({
  name
}: TurbineNavigationParams): string => {
  if (!name) {
    return '';
  }

  return `?name=${encodeURIComponent(name)}`;
};

export const buildTurbineQueryParamsFromSearchParams = (
  searchParams: URLSearchParams
): string => {
  return buildTurbineQueryParams({
    name: searchParams.get('name')
  });
};

export const buildTurbineBreadcrumb = ({
  turbineName,
  fromLocation
}: {
  turbineName: string;
  fromLocation?: TurbineFromLocationState | null;
}): NavigationBarItem[] => {
  const displayName = turbineName || '';

  if (fromLocation?.siteId && fromLocation?.locationName) {
    return [
      { label: 'Locations', path: navigationPaths.locations },
      {
        label: fromLocation.locationName,
        path: `${navigationPaths.locations}/${fromLocation.siteId}?name=${encodeURIComponent(fromLocation.locationName)}`
      },
      { label: displayName, path: '' }
    ];
  }

  return [
    { label: 'Turbines', path: navigationPaths.turbines },
    { label: displayName, path: '' }
  ];
};

export const isLocationTurbineBreadcrumb = (
  items: NavigationBarItem[]
): boolean =>
  items.length === 3 && items[0]?.path === navigationPaths.locations;

const TURBINE_BREADCRUMB_MAX_LENGTH = 7;
const LOCATIONS_BREADCRUMB_LABEL = 'Locations';
const LOCATIONS_BREADCRUMB_SHORT_LABEL = 'Loc...';
const BREADCRUMB_ELLIPSIS = '...';
const LOCATION_TURBINE_BREADCRUMB_TOTAL_MAX_LENGTH = 36;

type LocationTurbineBreadcrumbLabels = {
  locationsLabel: string;
  locationNameLabel: string;
  turbineLabel: string;
};

const formatTurbineLabel = (label: string, maxLength: number): string => {
  if (label.length <= maxLength) {
    return label;
  }

  return `${label.slice(0, maxLength)}${BREADCRUMB_ELLIPSIS}`;
};

const formatLocationsLabel = (turbineOverflow: number): string => {
  if (turbineOverflow <= 0) {
    return LOCATIONS_BREADCRUMB_LABEL;
  }

  return LOCATIONS_BREADCRUMB_SHORT_LABEL;
};

export const formatLocationTurbineBreadcrumbLabels = ({
  locationNameLabel,
  turbineLabel
}: {
  locationNameLabel: string;
  turbineLabel: string;
}): LocationTurbineBreadcrumbLabels => {
  const turbineOverflow = Math.max(
    0,
    turbineLabel.length - TURBINE_BREADCRUMB_MAX_LENGTH
  );

  let locationsLabel = formatLocationsLabel(turbineOverflow);
  let turbineMaxLength = TURBINE_BREADCRUMB_MAX_LENGTH;
  let formattedTurbineLabel = formatTurbineLabel(turbineLabel, turbineMaxLength);

  while (
    locationsLabel.length +
      locationNameLabel.length +
      formattedTurbineLabel.length >
      LOCATION_TURBINE_BREADCRUMB_TOTAL_MAX_LENGTH &&
    turbineMaxLength > 1
  ) {
    turbineMaxLength -= 1;
    formattedTurbineLabel = formatTurbineLabel(turbineLabel, turbineMaxLength);
  }

  return {
    locationsLabel,
    locationNameLabel,
    turbineLabel: formattedTurbineLabel
  };
};
